import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Boxes, ShieldCheck, Timer, ScanLine, TrendingUp, Activity, Lock } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1717386255767-52643970d483?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGxhc3RpYyUyMG1hbnVmYWN0dXJpbmclMjBtYWNoaW5lcnklMjB3YXJlaG91c2V8ZW58MHx8fHwxNzg4MTk2NDIxfDA&ixlib=rb-4.1.0&q=85";

const formatDetail = (d) => {
  if (d == null) return "Login failed. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
};

const TELEMETRY = [
  { label: "PET Bottling Lines", value: "Operational", tone: "text-signal-green" },
  { label: "Audit Trail", value: "Active", tone: "text-signal-green" },
  { label: "Session Security", value: "TLS 1.3 Encrypted", tone: "text-signal-amber" },
];

const FEATURES = [
  { icon: Timer, label: "Time Tracking" },
  { icon: ScanLine, label: "QR / Barcode" },
  { icon: TrendingUp, label: "Productivity" },
];

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [empId, setEmpId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [pinError, setPinError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    setPinError(null);
    setPinBusy(true);
    try {
      const res = await api.post("/auth/pin-login", { employee_id: empId, pin });
      setUser(res.data);
      navigate("/", { replace: true });
    } catch (err) {
      setPinError(formatDetail(err.response?.data?.detail));
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-[#0B0E14] text-foreground overflow-hidden" data-testid="login-page">
      <style>{`
        @keyframes loginRise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .login-rise { opacity: 0; animation: loginRise 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
      `}</style>

      {/* Hero panel */}
      <div className="lg:col-span-7 relative hidden lg:flex flex-col justify-between p-12 border-r border-border/40 overflow-hidden">
        <img src={HERO_IMG} alt="PET packaging plant floor" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/75 to-[#0B0E14]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14]/60 to-transparent" />

        <div className="relative login-rise" style={{ animationDelay: "0.05s" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Boxes className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-heading font-bold text-xl leading-none tracking-wide">WIPPLOPS</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-0.5">Worldites Operations Platform</p>
            </div>
          </div>
        </div>

        <div className="relative space-y-5">
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-primary login-rise" style={{ animationDelay: "0.15s" }}>
            Manufacturing · PET Bottles · Jars · Caps · Packaging
          </p>
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-black font-heading tracking-tight uppercase leading-[0.95] login-rise" style={{ animationDelay: "0.25s" }}>
            Every minute.<br />Every carton.<br /><span className="text-primary">Accounted for.</span>
          </h1>
          <p className="text-sm text-muted-foreground max-w-md login-rise" style={{ animationDelay: "0.35s" }}>
            A closed, management-controlled ERP. Only employees pre-approved by an
            administrator can sign in — authentication never equals authorisation.
          </p>
          <div className="flex flex-wrap gap-2.5 pt-2 login-rise" style={{ animationDelay: "0.45s" }}>
            {TELEMETRY.map((t) => (
              <div key={t.label} className="flex items-center gap-2 border border-border/60 bg-[#0B0E14]/70 backdrop-blur-sm rounded-md px-3 py-2">
                <Activity className={`w-3.5 h-3.5 ${t.tone}`} />
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{t.label}:</span>
                <span className={`text-[10px] font-mono uppercase tracking-widest ${t.tone}`}>{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Login portal */}
      <div className="lg:col-span-5 flex items-center justify-center p-6 md:p-12 bg-background/95 backdrop-blur-md relative z-10">
        <div className="w-full max-w-sm space-y-7">
          <div className="space-y-2 lg:hidden login-rise" style={{ animationDelay: "0.05s" }}>
            <div className="flex items-center gap-2">
              <Boxes className="w-8 h-8 text-primary" />
              <span className="font-heading font-bold text-2xl tracking-wide">WIPPLOPS</span>
            </div>
            <p className="text-sm text-muted-foreground">Worldites Operations Platform</p>
          </div>

          <div className="space-y-1.5 login-rise" style={{ animationDelay: "0.1s" }}>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Authorised Access</p>
            <h2 className="text-lg font-bold font-heading tracking-wide uppercase">Sign in to your console</h2>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center login-rise" style={{ animationDelay: "0.2s" }}>
            {FEATURES.map((f) => (
              <div key={f.label} className="border border-border rounded-lg p-3 bg-card hover:border-primary/50 transition-colors duration-200">
                <f.icon className="w-5 h-5 mx-auto text-primary" />
                <p className="text-[10px] font-mono uppercase tracking-widest mt-2 text-muted-foreground">{f.label}</p>
              </div>
            ))}
          </div>

          <form onSubmit={handlePasswordLogin} className="space-y-3.5 login-rise" style={{ animationDelay: "0.3s" }} data-testid="staff-login-form">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-primary" /> Official Email Login
            </p>
            <div>
              <Label htmlFor="login-email">Official Email</Label>
              <Input id="login-email" data-testid="login-email-input" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@worldites.com"
                className="mt-1.5 h-11 bg-card/60 focus-visible:ring-primary/60" />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input id="login-password" data-testid="login-password-input" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="mt-1.5 h-11 bg-card/60 focus-visible:ring-primary/60" />
            </div>
            {error && <p className="text-sm text-signal-red" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy}
              className="w-full h-11 text-sm font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-200 hover:shadow-[0_0_24px_-6px_hsl(25,95%,53%)]"
              data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in with Email"}
            </Button>
          </form>

          <div className="flex items-center gap-3 login-rise" style={{ animationDelay: "0.38s" }}>
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handlePinLogin} className="space-y-3.5 login-rise" style={{ animationDelay: "0.46s" }} data-testid="pin-login-form">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
              <ScanLine className="w-3.5 h-3.5 text-signal-orange" /> Factory / Warehouse Login
            </p>
            <div>
              <Label htmlFor="pin-emp-id">Employee ID</Label>
              <Input id="pin-emp-id" data-testid="pin-employee-id-input" required autoComplete="off" value={empId}
                onChange={(e) => setEmpId(e.target.value.toUpperCase())} placeholder="WIP-102"
                className="mt-1.5 h-11 font-mono bg-card/60 focus-visible:ring-signal-orange/60" />
            </div>
            <div>
              <Label htmlFor="pin-code">Secure PIN</Label>
              <Input id="pin-code" data-testid="pin-input" type="password" required inputMode="numeric" maxLength={4}
                autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••"
                className="mt-1.5 h-11 font-mono tracking-[0.5em] bg-card/60 focus-visible:ring-signal-orange/60" />
            </div>
            {pinError && <p className="text-sm text-signal-red" data-testid="pin-login-error">{pinError}</p>}
            <Button type="submit" disabled={pinBusy}
              className="w-full h-11 text-sm font-semibold bg-signal-orange hover:bg-signal-orange/90 text-white transition-all duration-200 hover:shadow-[0_0_24px_-6px_#F97316]"
              data-testid="pin-login-button">
              {pinBusy ? "Verifying…" : "Login"}
            </Button>
          </form>

          <div className="pt-2 space-y-2 login-rise" style={{ animationDelay: "0.55s" }}>
            <div className="flex items-center justify-center gap-2 border border-signal-green/30 bg-signal-green/5 rounded-md px-3 py-2.5">
              <ShieldCheck className="w-4 h-4 text-signal-green" />
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-signal-green">Private &amp; Authorised Users Only</p>
            </div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60 text-center">
              No public registration · Every login is audited
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
